













import { VNodeData } from 'vue';
import {
  computed,
  defineComponent,
  getCurrentInstance,
  h,
  inject,
  nextTick,
  provide,
  ref,
  watch
} from '@vue/composition-api';
import {
  Connection,
  EdgeComponent,
  HandleElement,
  HandleType,
  MouseTouchEvent
} from '../../types';
import { ConnectionMode, Position } from '../../types';
import { useEdgeHooks, useHandle, useWorkFlow } from '../../composables';
import { EdgeId, EdgeRef, Slots } from '../../context';
import { getEdgeHandle, getHandlePosition, getMarkerId } from '../../utils';
import EdgeAnchor from './EdgeAnchor';
import VNodes from '@/components/common/VNodes';
import { Fragment } from 'vue-frag';

interface Props {
  id: string;
}

const WorkflowEdgeWrapper = defineComponent({
  name: 'WorkflowEdgeWrapper',

  components: {
    VNodes,
    Fragment
  },

  props: {
    id: { type: String }
  },

  setup(props: Props, { root }) {
    const {
      id: workFlowId,
      addSelectedEdges,
      connectionMode,
      edgeUpdaterRadius,
      emits,
      nodesSelectionActive,
      noPanClassName,
      getEdgeTypes,
      removeSelectedEdges,
      findEdge,
      findNode,
      isValidConnection,
      multiSelectionActive,
      disableKeyboardA11y,
      elementsSelectable,
      edgesUpdatable,
      edgesFocusable,
      hooks
    } = useWorkFlow();

    const edge = computed(() => findEdge(props.id)!);

    const { emit, on } = useEdgeHooks(edge.value, emits);

    const slots = inject(Slots);

    const instance = getCurrentInstance();

    const mouseOver = ref(false);

    const updating = ref(false);

    const nodeId = ref('');

    const handleId = ref<string | null>(null);

    const edgeUpdaterType = ref<HandleType>('source');

    const edgeEl = ref<SVGElement | null>(null);

    const isSelectable = computed(() =>
      typeof edge.value.selectable === 'undefined'
        ? elementsSelectable.value
        : edge.value.selectable
    );

    const isUpdatable = computed(() =>
      typeof edge.value.updatable === 'undefined'
        ? edgesUpdatable.value
        : edge.value.updatable
    );

    const isFocusable = computed(() =>
      typeof edge.value.focusable === 'undefined'
        ? edgesFocusable.value
        : edge.value.focusable
    );

    provide(EdgeId, props.id);
    provide(EdgeRef, edgeEl);

    const edgeClass = computed(() =>
      edge.value.class instanceof Function
        ? edge.value.class(edge.value)
        : edge.value.class
    );
    const edgeStyle = computed(() =>
      edge.value.style instanceof Function
        ? edge.value.style(edge.value)
        : edge.value.style
    );

    const { handlePointerDown } = useHandle({
      nodeId,
      handleId,
      type: edgeUpdaterType,
      isValidConnection,
      edgeUpdaterType,
      onEdgeUpdate,
      onEdgeUpdateEnd
    });

    function onEdgeUpdaterMouseEnter() {
      mouseOver.value = true;
    }

    function onEdgeUpdaterMouseOut() {
      mouseOver.value = false;
    }

    function onEdgeUpdate(event: MouseTouchEvent, connection: Connection) {
      emit.update({ event, edge: edge.value, connection });
    }

    function onEdgeUpdateEnd(event: MouseTouchEvent) {
      emit.updateEnd({ event, edge: edge.value });
      updating.value = false;
    }

    function handleEdgeUpdater(event: MouseEvent, isSourceHandle: boolean) {
      if (event.button !== 0) {
        return;
      }

      updating.value = true;

      nodeId.value = isSourceHandle ? edge.value.target : edge.value.source;
      handleId.value =
        (isSourceHandle ? edge.value.targetHandle : edge.value.sourceHandle) ??
        '';

      edgeUpdaterType.value = isSourceHandle ? 'target' : 'source';

      emit.updateStart({ event, edge: edge.value });

      handlePointerDown(event);
    }

    function onEdgeClick(event: MouseEvent) {
      const data = { event, edge: edge.value };

      if (isSelectable.value) {
        nodesSelectionActive.value = false;

        if (edge.value.selected && multiSelectionActive.value) {
          removeSelectedEdges([edge.value]);

          edgeEl.value?.blur();
        } else {
          removeSelectedEdges();

          nextTick(() => {
            addSelectedEdges([edge.value]);
          });
        }
      }

      emit.click(data);
    }

    function onEdgeContextMenu(event: MouseEvent) {
      emit.contextMenu({ event, edge: edge.value });
    }

    function onDoubleClick(event: MouseEvent) {
      emit.doubleClick({ event, edge: edge.value });
    }

    function onEdgeMouseEnter(event: MouseEvent) {
      emit.mouseEnter({ event, edge: edge.value });
    }

    function onEdgeMouseMove(event: MouseEvent) {
      emit.mouseMove({ event, edge: edge.value });
    }

    function onEdgeMouseLeave(event: MouseEvent) {
      emit.mouseLeave({ event, edge: edge.value });
    }

    function onEdgeUpdaterSourceMouseDown(event: MouseEvent) {
      handleEdgeUpdater(event, true);
    }

    function onEdgeUpdaterTargetMouseDown(event: MouseEvent) {
      handleEdgeUpdater(event, false);
    }

    // function onKeyDown(event: KeyboardEvent) {
    //   if (!disableKeyboardA11y.value && elementSelectionKeys.includes(event.key) && isSelectable.value) {
    //     const unselect = event.key === 'Escape'

    //     if (unselect) {
    //       edgeEl.value?.blur()

    //       removeSelectedEdges([findEdge(props.id)!])
    //     } else {
    //       addSelectedEdges([findEdge(props.id)!])
    //     }
    //   }
    // }

    const sourceNode = computed(() => findNode(edge.value.source));
    const targetNode = computed(() => findNode(edge.value.target));
    const pathOptions = computed(() =>
      'pathOptions' in edge.value ? edge.value.pathOptions : {}
    );

    const sourceNodeHandles = computed<HandleElement[] | undefined>(() => {
      if (connectionMode.value === ConnectionMode.Strict) {
        return sourceNode.value?.handleBounds.source;
      } else {
        return [
          ...(sourceNode.value?.handleBounds.source || []),
          ...(sourceNode.value?.handleBounds.target || [])
        ];
      }
    });

    const sourceHandle = computed(() =>
      getEdgeHandle(sourceNodeHandles.value, edge.value.sourceHandle)
    );

    const targetNodeHandles = computed<HandleElement[] | undefined>(() => {
      if (connectionMode.value === ConnectionMode.Strict) {
        return targetNode.value?.handleBounds.target;
      } else {
        return [
          ...(targetNode.value?.handleBounds.target || []),
          ...(targetNode.value?.handleBounds.source || [])
        ];
      }
    });

    const targetHandle = computed(() =>
      getEdgeHandle(targetNodeHandles.value, edge.value.targetHandle)
    );

    const sourcePosition = computed(
      () => sourceHandle.value?.position || Position.Bottom
    );

    const targetPosition = computed(
      () => targetHandle.value?.position || Position.Top
    );

    const sourceHandlePosition = computed(() =>
      getHandlePosition(
        sourceNode.value!,
        sourceHandle.value,
        sourcePosition.value
      )
    );
    const targetHandlePosition = computed(() =>
      getHandlePosition(
        targetNode.value!,
        targetHandle.value,
        targetPosition.value
      )
    );

    watch(sourceHandlePosition, value => {
      edge.value.sourceX = value.x;
      edge.value.sourceY = value.y;
    });

    watch(targetHandlePosition, value => {
      edge.value.targetX = value.x;
      edge.value.targetY = value.y;
    });

    const label = computed(() => edge.value.data.name || edge.value.label);

    const PathEdge = computed(() => {
      const name = edge.value.type || 'default';

      const slot = slots?.[`edge-${name}`];
      if (slot) {
        return slot;
      }

      let edgeType = edge.value.template ?? getEdgeTypes.value[name];

      if (edgeType && typeof edgeType !== 'string') {
        return edgeType;
      }

      return false;
    });

    const EdgeComponent = computed(() =>
      h(Fragment, [
        updating.value
          ? null
          : h(
              PathEdge.value
                ? (PathEdge.value as EdgeComponent)
                : getEdgeTypes.value.default,
              {
                style: edgeStyle.value!,
                props: {
                  id: props.id,
                  sourceNode: sourceNode.value,
                  targetNode: targetNode.value,
                  source: edge.value.source,
                  target: edge.value.target,
                  type: edge.value.type,
                  updatable: isUpdatable.value,
                  selected: edge.value.selected,
                  animated: edge.value.animated,
                  label: label.value,
                  labelStyle: edge.value.labelStyle,
                  labelShowBg: edge.value.labelShowBg,
                  labelBgStyle: edge.value.labelBgStyle,
                  labelBgPadding: edge.value.labelBgPadding,
                  labelBgBorderRadius: edge.value.labelBgBorderRadius,
                  data: edge.value.data,
                  events: { ...edge.value.events, ...on },
                  markerStart: `url('#${getMarkerId(
                    edge.value.markerStart,
                    workFlowId
                  )}')`,
                  markerEnd: `url('#${getMarkerId(
                    edge.value.markerEnd,
                    workFlowId
                  )}')`,
                  sourcePosition: sourcePosition.value,
                  targetPosition: targetPosition.value,
                  sourceX: sourceHandlePosition.value.x,
                  sourceY: sourceHandlePosition.value.y,
                  targetX: targetHandlePosition.value.x,
                  targetY: targetHandlePosition.value.y,
                  sourceHandleId: edge.value.sourceHandle,
                  targetHandleId: edge.value.targetHandle,
                  interactionWidth: edge.value.interactionWidth,
                  ...pathOptions.value
                }
              }
            ),
        [
          isUpdatable.value === 'source' || isUpdatable.value === true
            ? [
                h(
                  'g',
                  {
                    on: {
                      mousedown: onEdgeUpdaterSourceMouseDown,
                      mouseenter: onEdgeUpdaterMouseEnter,
                      mouseout: onEdgeUpdaterMouseOut
                    }
                  },
                  [
                    h(EdgeAnchor, {
                      props: {
                        position: sourcePosition.value,
                        centerX: sourceHandlePosition.value.x,
                        centerY: sourceHandlePosition.value.y,
                        radius: edgeUpdaterRadius.value,
                        type: 'source',
                        'data-type': 'source'
                      }
                    })
                  ]
                )
              ]
            : null,
          isUpdatable.value === 'target' || isUpdatable.value === true
            ? [
                h(
                  'g',
                  {
                    on: {
                      mousedown: onEdgeUpdaterTargetMouseDown,
                      mouseenter: onEdgeUpdaterMouseEnter,
                      mouseout: onEdgeUpdaterMouseOut
                    }
                  },
                  [
                    h(EdgeAnchor, {
                      props: {
                        position: targetPosition.value,
                        centerX: targetHandlePosition.value.x,
                        centerY: targetHandlePosition.value.y,
                        radius: edgeUpdaterRadius.value,
                        type: 'target',
                        'data-type': 'target'
                      }
                    })
                  ]
                )
              ]
            : null
        ]
      ])
    );
    // };

    const edgeWrapperClasses = computed<VNodeData['class']>(() => [
      'workflow__edge',
      `workflow__edge-default`,
      noPanClassName.value,
      edgeClass.value,
      {
        updating: mouseOver.value,
        selected: edge.value.selected,
        animated: edge.value.animated,
        inactive: !isSelectable.value && !hooks.value.edgeClick.hasListeners()
      }
    ]);

    const edgeWrapperProps = computed<VNodeData['attrs']>(() => ({
      'data-id': props.id,
      tabIndex: isFocusable.value ? 0 : undefined,
      'aria-label':
        edge.value.ariaLabel === null
          ? undefined
          : edge.value.ariaLabel ||
            `Edge from ${edge.value.source} to ${edge.value.target}`,
      // 'aria-describedby': isFocusable.value ? `${ARIA_EDGE_DESC_KEY}-${workFlowId}` : undefined,
      role: isFocusable.value ? 'button' : 'img'
    }));

    const edgeWrapperListeners = computed<VNodeData['on']>(() => {
      return {
        click: onEdgeClick,
        contextmenu: onEdgeContextMenu,
        dblclick: onDoubleClick,
        mouseenter: onEdgeMouseEnter,
        mousemove: onEdgeMouseMove,
        mouseleave: onEdgeMouseLeave
      };
    });

    const isEdgeVisible = computed(() => {
      if (!sourceNode.value && !targetNode.value) {
        //   // emits.error(new VueFlowError(ErrorCode.EDGE_SOURCE_TARGET_MISSING, edge.value.id, edge.value.source, edge.value.target))
        return false;
      }

      if (!sourceNode.value) {
        //   // emits.error(new VueFlowError(ErrorCode.EDGE_SOURCE_MISSING, edge.value.id, edge.value.source))
        return false;
      }

      if (!targetNode.value) {
        //   // emits.error(new VueFlowError(ErrorCode.EDGE_TARGET_MISSING, edge.value.id, edge.value.target))
        return false;
      }

      if (
        !edge.value ||
        edge.value.hidden ||
        sourceNode.value?.hidden ||
        targetNode.value?.hidden
      ) {
        return false;
      }

      return true;
    });

    return {
      isEdgeVisible,
      edgeEl,
      edgeWrapperClasses,
      edgeWrapperProps,
      edgeWrapperListeners,
      EdgeComponent
    };
  }
});

export default WorkflowEdgeWrapper;
